import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { Users, UserMinus, Clock, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format } from 'date-fns';

export default function TeamDashboard() {
  const { user } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'team'],
    queryFn: async () => {
      const res = await dashboardApi.team();
      return res.data.data;
    },
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="h-24 bg-slate-100 rounded-xl animate-pulse"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-8">
      <div className="flex justify-end">
        <Link to="/approvals/inbox">
          <Button variant="primary" className="shadow-sm">Go to Approval Inbox</Button>
        </Link>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="card p-5 border-l-4 border-l-blue-500">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Team Size</p>
              <h3 className="text-3xl font-bold text-slate-800">{data.teamSize}</h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-5 border-l-4 border-l-amber-500">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">On Leave Today</p>
              <h3 className="text-3xl font-bold text-slate-800">{data.onLeaveToday}</h3>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg">
              <UserMinus className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="card p-5 border-l-4 border-l-green-500">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Current Coverage</p>
              <h3 className="text-3xl font-bold text-slate-800">{data.coveragePct || 0}%</h3>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <Users className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-5 border-l-4 border-l-purple-500 relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-purple-50 rounded-bl-full translate-x-4 -translate-y-4 transition-transform group-hover:scale-110"></div>
          <div className="relative z-10 flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Pending Approvals</p>
              <h3 className="text-3xl font-bold text-slate-800">{data.pendingApprovals}</h3>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="card p-6 mb-8">
        <h2 className="text-lg font-bold text-slate-800 mb-6">Top Leave Takers</h2>
        <div className="space-y-5">
          {data.topLeaveTakers?.map((member: any, i: number) => (
            <Link key={member.id} to={`/admin/reports?tab=BALANCE&userId=${member.id}`} className="flex items-center gap-4 group">
              <span className="w-6 text-center font-bold text-slate-300 text-lg">#{i + 1}</span>
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                {member.photoUrl ? <img src={member.photoUrl} alt={member.name} className="w-full h-full rounded-full object-cover" /> : <span className="text-sm font-bold text-slate-500">{member.name?.charAt(0)}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-end mb-1">
                  <h4 className="font-semibold text-slate-800 truncate group-hover:text-blue-600">{member.name}</h4>
                  <span className="font-bold text-slate-700">{member.totalDays} days</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden"><div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${Math.min(100, (member.totalDays / 30) * 100)}%` }} /></div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Coverage Chart */}
        <div className="card p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800">7-Day Coverage Forecast</h2>
              <p className="text-xs text-slate-400 mt-0.5">% of team available each day · Red &lt;60% · Amber &lt;80% · Green ≥80%</p>
            </div>
          </div>
          {(!data.coverageForecast || data.coverageForecast.every((d: any) => d.coveragePercentage === 100)) && (
            <p className="text-sm text-slate-400 text-center mt-4">No leaves in the next 7 days — full team available ✅</p>
          )}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.coverageForecast || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#64748b' }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#64748b' }} domain={[0, 100]} />
                <Tooltip 
                  cursor={{ fill: isDark ? '#253047' : '#f1f5f9' }}
                  contentStyle={{ borderRadius: '12px', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)', backgroundColor: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#e2e8f0' : '#1e293b' }}
                  formatter={(value: any) => [`${value}%`, 'Coverage']}
                />
                <Bar dataKey="coveragePercentage" radius={[4, 4, 0, 0]}>
                  {data.coverageForecast?.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={(entry.coveragePercentage || 0) < 60 ? '#ef4444' : (entry.coveragePercentage || 0) < 80 ? '#f59e0b' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Upcoming Leaves List */}
        <div className="card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" /> Upcoming Team Leaves
            </h2>
            <Link to="/approvals/team-calendar" className="text-sm font-medium text-blue-600 hover:text-blue-700">
              View Calendar
            </Link>
          </div>
          
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {!(data.upcomingTeamLeaves?.length) ? (
              <div className="text-center py-12">
                <CalendarDays className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No team leaves in the next 14 days.</p>
              </div>
            ) : (
              data.upcomingTeamLeaves?.map((leave: any) => (
                <div key={leave.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex flex-col items-center justify-center shrink-0 border border-blue-100 text-blue-800">
                    <span className="text-[10px] font-bold uppercase">{format(new Date(leave.fromDate), 'MMM')}</span>
                    <span className="text-lg font-black leading-none">{format(new Date(leave.fromDate), 'd')}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-800 truncate">{leave.user.name}</h4>
                    <p className="text-sm text-slate-500 truncate flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: leave.leaveType.colour }}></span>
                      {leave.leaveType.name} • {leave.duration} day(s)
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
